/** Key rebinding scene — lets the player remap any action to a new key and persists changes via KeyBindings. */
import Phaser from 'phaser';
import { KeyBindings, GameAction } from '../systems/KeyBindings';
import { crispText, crispButton, setupCrispCamera } from '../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor } from '../ui/Theme';
import { GAME_WIDTH } from '../utils/Constants';

const REMAPPABLE_ACTIONS: GameAction[] = [
  GameAction.MoveUp,
  GameAction.MoveLeft,
  GameAction.MoveDown,
  GameAction.MoveRight,
  GameAction.Run,
  GameAction.Dash,
  GameAction.Skill1,
  GameAction.Skill2,
  GameAction.Skill3,
  GameAction.Skill4,
  GameAction.Skill5,
];

interface RemapRow {
  action: GameAction;
  keyText: Phaser.GameObjects.Text;
}

/**
 * Renders one interactive row per remappable action. Clicking a row enters listening mode;
 * the next keypress becomes the new primary binding, with conflict resolution via KeyBindings.setPrimary().
 */
export class KeyRemapScene extends Phaser.Scene {
  private rows: RemapRow[] = [];
  private listeningFor: GameAction | null = null;
  private promptText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'KeyRemapScene' });
  }

  create(): void {
    this.rows = [];
    this.listeningFor = null;

    setupCrispCamera(this);
    const width = GAME_WIDTH;

    // Background
    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 40, 'KEY BINDINGS', {
      fontFamily: Font.family, fontSize: Font.sizeTitle, color: Hex.greenBright,
    }).setOrigin(0.5);

    this.promptText = crispText(this, width / 2, 76, '', {
      fontFamily: Font.family, fontSize: Font.sizeSmall, color: '#ffdd00',
    }).setOrigin(0.5);

    const startY = 120;
    const lineHeight = 32;
    const actionColX = width / 2 - 20;
    const keyColX = width / 2 + 20;

    for (let i = 0; i < REMAPPABLE_ACTIONS.length; i++) {
      const action = REMAPPABLE_ACTIONS[i];
      const y = startY + i * lineHeight;

      crispText(this, actionColX, y, KeyBindings.actionLabel(action), TextStyle.body)
        .setOrigin(1, 0.5);

      const keyText = crispText(this, keyColX, y, KeyBindings.displayLabel(action), TextStyle.bodyKey)
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true });

      keyText.on('pointerover', () => {
        if (this.listeningFor !== action) keyText.setColor(Hex.greenBright);
      });
      keyText.on('pointerout', () => {
        if (this.listeningFor !== action) keyText.setColor(Hex.greenDefault);
      });
      keyText.on('pointerdown', () => this.startListening(action));

      this.rows.push({ action, keyText });
    }

    // Buttons
    const btnY = startY + REMAPPABLE_ACTIONS.length * lineHeight + 50;

    crispButton(this, width / 2, btnY, '[ RESET ]',
      Font.sizeButtonSm, ButtonColor.warning, () => this.resetBindings());

    // Global key listener for rebinding and ESC-to-exit
    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      if (event.keyCode === Phaser.Input.Keyboard.KeyCodes.ESC) {
        if (this.listeningFor !== null) {
          this.cancelListening();
        } else {
          this.goBack();
        }
        return;
      }

      if (this.listeningFor === null) return;
      this.applyBinding(event.keyCode);
    });
  }

  private startListening(action: GameAction): void {
    this.listeningFor = action;
    this.promptText.setText(`Press a key for ${KeyBindings.actionLabel(action)}...  (ESC to cancel)`);

    const row = this.rows.find(r => r.action === action);
    if (row) {
      row.keyText.setText('...');
      row.keyText.setColor('#ffdd00');
    }
  }

  private applyBinding(keyCode: number): void {
    if (this.listeningFor === null) return;

    const conflict = KeyBindings.setPrimary(this.listeningFor, keyCode);
    KeyBindings.save();
    this.refreshLabels();
    this.listeningFor = null;

    if (conflict) {
      this.promptText.setText(
        `Unbound ${KeyBindings.keyLabel(keyCode)} from ${KeyBindings.actionLabel(conflict)}`
      );
    } else {
      this.promptText.setText('');
    }
  }

  private cancelListening(): void {
    this.listeningFor = null;
    this.promptText.setText('');
    this.refreshLabels();
  }

  private resetBindings(): void {
    KeyBindings.resetToDefaults();
    KeyBindings.save();
    this.refreshLabels();
    this.promptText.setText('Reset to defaults.');
  }

  private refreshLabels(): void {
    for (const row of this.rows) {
      row.keyText.setText(KeyBindings.displayLabel(row.action));
      row.keyText.setColor(Hex.greenDefault);
    }
  }

  private goBack(): void {
    KeyBindings.save();
    this.scene.start('MenuScene');
  }
}
